import { ReactElement, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from 'react-bootstrap/Button';
import { useFlags } from '@flagsmith/flagsmith/react';

import { ApiRoute, Route } from 'config';
import { runsWebview } from 'utils/device';
import { patch } from 'utils/network';
import { monthDay } from 'utils/dateTimes';
import useCurrentUser from 'hooks/useCurrentUser';
import useAddresses from 'hooks/useAddresses';

import { Component as CirclePauseIcon } from 'icons/icon-circle--pause.svg';
import CancelModalContainer from 'platform/components/cancellation/CancelModalContainer';
import SubscriptionStatus from '../SubscriptionStatus';

type Props = {
  handleErrors: () => void;
};

function ReactivateSubscription({ handleErrors }: Props): ReactElement {
  const { modal_cancellation_flow: modalCancellationFlow } = useFlags(['modal_cancellation_flow']);
  const isModalCancellationEnabled = modalCancellationFlow?.enabled && modalCancellationFlow?.value === 'show';
  const {
    currentUser,
    reload,
  } = useCurrentUser();
  const {
    subscription,
    subscriptionStatus,
    hasValidPaymentMethod,
    isSuspended,
    isReturning,
  } = currentUser;
  const navigate = useNavigate();

  const {
    returning_reactivation_on: returningReactivationOn,
  } = subscription || {};

  const { addresses } = useAddresses();
  const hasValidPrimaryAddress = addresses[0]?.isValid;

  const [showModalCancellation, setShowModalCancellation] = useState(false);

  const reActivateSubscription = async () => {
    if (!hasValidPrimaryAddress) {
      navigate(Route.PROFILE_ADD_ADDRESS);
    } else if (!hasValidPaymentMethod || isSuspended) {
      navigate(Route.PROFILE_EDIT_PAYMENT);
    } else {
      const response = await patch(ApiRoute.REACTIVATE);

      if (response.ok) {
        reload();
        navigate(Route.PROFILE);
      } else {
        navigate(Route.PROFILE_EDIT_PAYMENT);
      }
    }
  };

  return (
    <>
      <SubscriptionStatus subscriptionStatus={subscriptionStatus} />

      <div
        id="subscription-management-reactivate"
        className={[
          'c-subscription-management-option',
          'is-active',
          'is-disabled',
        ].join(' ')}
        aria-controls="pause-subscription-content"
      >
        <div className="c-subscription-management-option__content">
          <div
            className={[
              'u-text',
              'u-text--center',
              'c-subscription-management-option__header',
              'disabled',
            ].join(' ')}
          >
            <div className="c-subscription-management-option__icon">
              <CirclePauseIcon
                height="36"
                width="36"
                className="c-svg-fill-gray-068-color"
              />
            </div>
            <h3 className="c-heading c-heading--brand-7">
              RE-ACTIVATE YOUR SUBSCRIPTION
            </h3>

            {isReturning && (
              <p className="u-text u-text--lg u-text--italic u-text--error">
                Your account is currently paused until&nbsp;
                {monthDay(new Date(returningReactivationOn), false, true)}
              </p>
            )}

            <Button
              className="subscription-management-reactivate__button d-block c-button c-button--primary"
              onClick={reActivateSubscription}
            >
              Re-Activate Subscription
            </Button>
          </div>
        </div>
      </div>

      {isReturning && (
        <div
          className={[
            'c-subscription-management-cancel-link__wrapper',
            runsWebview ? 'd-none' : '',
          ].join(' ')}
        >
          {isModalCancellationEnabled ? (
            <Button
              className="c-subscription-management-cancel-button"
              onClick={() => setShowModalCancellation(true)}
            >
              <span>
                Cancel Subscription
              </span>
            </Button>
          ) : (
            <Link
              to={`${Route.MANAGE_SUBSCRIPTION}?action=cancel`}
              className="c-subscription-management-cancel-link"
            >
              <span>
                Cancel Subscription
              </span>
            </Link>
          )}
        </div>
      )}

      {showModalCancellation && (
        <CancelModalContainer
          show={showModalCancellation}
          onClose={() => setShowModalCancellation(false)}
          handleErrors={handleErrors}
        />
      )}
    </>
  );
}

export default ReactivateSubscription;
